import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import Navigation from './Navigation';
import InfoCard from './InfoCard';
import StoreCard from './StoreCard';
import SettingsModal from './SettingsModal';
import ExpenseModal from './ExpenseModal';
import ExpenseListModal from './ExpenseListModal';
import '../styles/stores.css';

function StoresPage({ onLogout, onNavigate, userName, people: propsPeople, onUpdatePerson }) {
  // Generate 50 people with codes 001-050
  const generatePeople = () => {
    return Array.from({ length: 50 }, (_, i) => {
      const code = String(i + 1).padStart(3, '0');
      return {
        id: code,
        code: code,
        name: `Person ${code}`,
        tc: `1234567890${i}`,
        birth: "15.03.1985",
        addr: `Address ${code}, Istanbul`,
        phone: `+90 555 ${code} 45 67`,
        srvIP: `192.168.1.${100 + i}`,
        srvPass: `Server${code}@2024`,
        routingNum: `02100002${i}`,
        accountNum: `12345678901${i}`,
        bankPass: `Bank${code}@2024`,
        cardNum: `52345678901234${String(i).padStart(2, '0')}`,
        cardExp: "12/27",
        cardCVV: "456",
        crypto: `0x${code}a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0`,
        stores: Array.from({ length: 10 }, (_, j) => ({
          num: j + 1,
          storeCode: `${code}-${String(j + 1).padStart(2, '0')}`,
          name: `Store ${code}-${String(j + 1).padStart(2, '0')}`,
          email: `store${code}-${j + 1}@batchlabel.com`,
          pass: `Store${code}-${j + 1}@2024`,
          bal: Math.floor(Math.random() * 50000),
          transactions: []
        }))
      };
    });
  };

  // Direkt props'tan kullan (useState yok!)
  const people = propsPeople && propsPeople.length > 0 ? propsPeople : generatePeople();
  
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPerson, setEditingPerson] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showExpenseListModal, setShowExpenseListModal] = useState(false);

  const selectedPerson = people.find(p => p.id === selectedPersonId);

  // Filter people by search
  const filteredPeople = people.filter(person => {
    const search = searchTerm.toLowerCase();
    return (
      person.code.toLowerCase().includes(search) ||
      person.name.toLowerCase().includes(search)
    );
  });

  const handleEditPerson = (person) => {
    setEditingPerson(person);
  };

  const handleUpdatePerson = (updatedData) => {
    if (onUpdatePerson) {
      onUpdatePerson(updatedData);
    }
    setEditingPerson(null);
    alert(`Person #${updatedData.code} updated successfully!`);
  };

  const handleUpdateStore = (storeNum, storeData) => {
    const updatedPerson = {
      ...selectedPerson,
      stores: (selectedPerson.stores || []).map(s => 
        s.num === storeNum 
          ? { 
              ...s, 
              name: storeData.name, 
              bal: storeData.bal,
              transactions: storeData.transactions || s.transactions || []
            }
          : s
      )
    };
    
    if (onUpdatePerson) {
      onUpdatePerson(updatedPerson);
    }
  };

  const handleAddExpense = (expense) => {
    const existingExpenses = selectedPerson.expenses || [];
    
    let updatedExpenses;
    if (editingExpense) {
      // Update existing expense
      updatedExpenses = existingExpenses.map(e => 
        e.id === editingExpense.id ? { ...expense, id: editingExpense.id } : e
      );
      setEditingExpense(null);
    } else {
      // Add new expense
      updatedExpenses = [...existingExpenses, expense];
    }
    
    const updatedPerson = {
      ...selectedPerson,
      expenses: updatedExpenses
    };
    if (onUpdatePerson) {
      onUpdatePerson(updatedPerson);
    }
    setShowExpenseModal(false);
  };

  const handleUpdateExpenses = (updatedExpenses) => {
    const updatedPerson = {
      ...selectedPerson,
      expenses: updatedExpenses
    };
    if (onUpdatePerson) {
      onUpdatePerson(updatedPerson);
    }
  };

  // If person selected, show detail view
  if (selectedPerson) {
    const storesTotal = (selectedPerson.stores || []).reduce((sum, s) => sum + (s.bal || 0), 0);
    const expensesTotal = (selectedPerson.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalBalance = storesTotal - expensesTotal;

    return (
      <div>
        <Navigation 
          currentPage="stores" 
          onNavigate={onNavigate}
          onLogout={onLogout}
          userName={userName}
        />
        
        <div className="container">
          {/* Modern Header Bar */}
          <div className="person-detail-header-modern">
            <button className="header-btn-back" onClick={() => setSelectedPersonId(null)} title="Back to all people">
              <span className="btn-icon-back">←</span>
            </button>
            
            <div className="header-person-info">
              <div className="header-person-code">#{selectedPerson.code || selectedPerson.id || '?'}</div>
              <div className="header-person-name">{selectedPerson.name || 'Unknown'}</div>
            </div>

            <button 
              type="button"
              className="header-balance header-balance-clickable"
              onClick={() => setShowExpenseModal(true)}
              title="Click to add expense"
            >
              💰 ${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </button>

            {/* Expenses Button */}
            <button
              type="button"
              onClick={() => setShowExpenseListModal(true)}
              title="View all expenses"
              style={{
                padding: '12px 16px',
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '8px',
                color: '#ff6b6b',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 107, 107, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 107, 107, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 107, 0.3)';
              }}
            >
              💸 Expenses
            </button>

            <button className="header-btn-edit" onClick={() => handleEditPerson(selectedPerson)} title="Edit person">
              <span className="btn-icon-edit">✏️</span>
            </button>
          </div>

          <div className="cards">
            <InfoCard type="id" person={selectedPerson} />
            <InfoCard type="bank" person={selectedPerson} />
            <InfoCard type="credit" person={selectedPerson} />
            <InfoCard type="server" person={selectedPerson} />
          </div>

          <div className="stores">
            {(selectedPerson.stores || [])
              .sort((a, b) => (a.num || 0) - (b.num || 0))
              .map((store, idx) => (
                <StoreCard 
                  key={store.storeCode || store.code || `store-${idx}`}
                  store={store}
                  personId={selectedPerson.id}
                  onUpdate={handleUpdateStore}
                />
              ))}
          </div>
        </div>

        {editingPerson && (
          <SettingsModal 
            onClose={() => setEditingPerson(null)}
            onSave={handleUpdatePerson}
            editPerson={editingPerson}
          />
        )}
        {showExpenseModal && (
          <ExpenseModal
            person={selectedPerson}
            onClose={() => {
              setShowExpenseModal(false);
              setEditingExpense(null);
            }}
            onSave={handleAddExpense}
            existingExpense={editingExpense}
          />
        )}
        {showExpenseListModal && (
          <ExpenseListModal
            person={selectedPerson}
            expenses={selectedPerson.expenses || []}
            onClose={() => setShowExpenseListModal(false)}
            onUpdate={handleUpdateExpenses}
          />
        )}
      </div>
    );
  }

  // Grid view - show all people
  return (
    <div>
      <Navigation 
        currentPage="stores" 
        onNavigate={onNavigate}
        onLogout={onLogout}
        userName={userName}
      />
      
      <div className="container">
        <div className="stores-page-header">
          <div className="stores-header-left">
            <h1 className="stores-page-title">👥 People & Stores</h1>
            <p className="stores-page-subtitle">
              {filteredPeople.length} {filteredPeople.length === 1 ? 'person' : 'people'} 
              {searchTerm && ` found for "${searchTerm}"`}
            </p>
          </div>
          <div className="stores-stats-mini">
            <div className="stat-mini">
              <div className="stat-mini-value">{people.length}</div>
              <div className="stat-mini-label">Total People</div>
            </div>
            <div className="stat-mini">
              <div className="stat-mini-value">
                {people.reduce((sum, p) => sum + (p.stores?.filter(s => s.email && s.email.trim() !== '').length || 0), 0)}
              </div>
              <div className="stat-mini-label">Active Stores</div>
            </div>
            <div className="stat-mini">
              <div className="stat-mini-value">
                ${formatCurrency(people.reduce((sum, p) => {
                  const storesTotal = (p.stores || []).reduce((s, st) => s + (st.bal || 0), 0);
                  const expensesTotal = (p.expenses || []).reduce((s, e) => s + (e.amount || 0), 0);
                  return sum + (storesTotal - expensesTotal);
                }, 0))}
              </div>
              <div className="stat-mini-label">Total Balance</div>
            </div>
          </div>
        </div>

        <div className="stores-search-bar">
          <div className="search-icon">🔍</div>
          <input
            type="text"
            className="stores-search-input-modern"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>×</button>
          )}
        </div>

        <div className="people-grid-modern">
          {filteredPeople
            .sort((a, b) => a.code.localeCompare(b.code))
            .map(person => {
            const storesTotal = (person.stores || []).reduce((sum, s) => sum + (s.bal || 0), 0);
            const expensesTotal = (person.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
            const totalBalance = storesTotal - expensesTotal;
            const activeStores = (person.stores || []).filter(s => s.email && s.email.trim() !== '').length;
            
            return (
              <div
                key={person.id}
                className="person-card-modern"
                onClick={() => setSelectedPersonId(person.id)}
              >
                <div className="person-card-header">
                  <div className="person-code-badge">#{person.code}</div>
                  {activeStores > 0 && (
                    <div className="active-badge">{activeStores} active</div>
                  )}
                </div>
                <div className="person-card-body">
                  <div className="person-name-modern">{person.name}</div>
                  <div className="person-balance-modern">
                    💰 ${formatCurrency(totalBalance)}
                  </div>
                  <div className="person-stores-info">
                    <span className="stores-count">{(person.stores || []).length} stores</span>
                    <span className="arrow-icon">→</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPeople.length === 0 && (
          <div className="empty-search-state">
            <div className="empty-search-icon">🔍</div>
            <div className="empty-search-text">No people found</div>
            <div className="empty-search-desc">Try a different search term</div>
            <button className="empty-search-btn" onClick={() => setSearchTerm('')}>
              Clear Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StoresPage;